import React, { useEffect, useMemo, useState } from "react";
import AdminSidebar from "./AdminSidebar";
import {
  getAdminPayments,
  getStudents,
  resolveStudentName,
  describeStudentRef,
} from "../api/adminclient";
import AdminTopbar from "./Admintopbar";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
  Promise.all([getAdminPayments(), getStudents()])
    .then(([p, s]) => {
      setPayments(p.data);
      setStudents(s.data);
    })
    .catch((err) => {
      console.error("Failed to load admin payments:", err);
      setError("Could not load payments from the server.");
    })
    .finally(() => setLoading(false));
}, []);

  useEffect(() => {
    setPage(1);
  }, [payments.length, pageSize]);

  // Pass the whole payment record so studentId / userId / email / stored name all work
const studentName = (payment) => payment.studentName || "—";

  const summary = useMemo(() => {
    const sum = (status) =>
      payments
        .filter((p) => p.status === status)
        .reduce((total, p) => total + Number(p.amount || 0), 0);
    const received = sum("SUCCESS");
    const refunded = sum("REFUNDED");
    const pending = sum("PENDING");
    return { received, refunded, pending, balance: received - refunded, count: payments.length };
  }, [payments]);

  const totalPages = Math.max(1, Math.ceil(payments.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const pagedPayments = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return payments.slice(start, start + pageSize);
  }, [payments, safePage, pageSize]);

  const rangeStart = payments.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, payments.length);

  

  const tone = (status) =>
    status === "SUCCESS" ? "active" : status === "PENDING" ? "pending" : "inactive";

  const goToPage = (n) => setPage(Math.min(Math.max(1, n), totalPages));

  const pageNumbers = useMemo(() => {
    const nums = [];
    const add = (n) => { if (n >= 1 && n <= totalPages && !nums.includes(n)) nums.push(n); };
    add(1);
    add(safePage - 1);
    add(safePage);
    add(safePage + 1);
    add(totalPages);
    return nums.sort((a, b) => a - b);
  }, [safePage, totalPages]);

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />
      <div className="admin-content">
        <AdminTopbar />
        <h2 className="h5 fw-bold mb-3">Payments</h2>

        {error && <div className="alert alert-warning py-2 small">{error}</div>}

        <div className="row g-3 mb-3">
          {[
            { label: "Received", value: summary.received, icon: "bi-cash-stack" },
            { label: "Pending", value: summary.pending, icon: "bi-hourglass-split" },
            { label: "Refunded", value: summary.refunded, icon: "bi-arrow-counterclockwise" },
            { label: "Net Balance", value: summary.balance, icon: "bi-wallet2" },
          ].map((c) => (
            <div className="col-6 col-md-3" key={c.label}>
              <div className="admin-stat-card">
                <div className="admin-stat-icon"><i className={`bi ${c.icon}`} /></div>
                <div className="admin-stat-value">₹{c.value.toLocaleString("en-IN")}</div>
                <div className="admin-stat-label">{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-card p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <h3 className="h6 fw-bold mb-0">Transactions</h3>
            <div className="d-flex align-items-center gap-2">
              <label className="small text-muted mb-0">Rows per page</label>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {loading && <p className="text-muted mb-0">Loading payments...</p>}
          {!loading && payments.length === 0 && <p className="text-muted small mb-0">No payments recorded.</p>}

          {!loading && payments.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Transaction ID</th>
                      <th>Student</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedPayments.map((p, i) => {
                      const name = studentName(p);
                      return (
                        <tr key={p.id}>
                          <td className="text-muted">{(safePage - 1) * pageSize + i + 1}</td>
                          <td>{p.transactionId}</td>
                          <td className="fw-semibold">
                            {name !== "—" ? (
                              name
                            ) : (
                              <span className="text-danger small fw-normal">
                                — (ref: {describeStudentRef(p)})
                              </span>
                            )}
                          </td>
                          <td>₹{Number(p.amount).toLocaleString("en-IN")}</td>
                          <td><span className={`admin-status-pill ${tone(p.status)}`}>{p.status}</span></td>
                          <td className="text-muted small">{p.date}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
                <div className="small text-muted">
                  Showing {rangeStart}–{rangeEnd} of {payments.length}
                </div>
                <nav aria-label="Payments pagination">
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => goToPage(safePage - 1)}>Prev</button>
                    </li>
                    {pageNumbers.map((n, idx) => {
                      const prev = pageNumbers[idx - 1];
                      const showEllipsis = prev !== undefined && n - prev > 1;
                      return (
                        <React.Fragment key={n}>
                          {showEllipsis && (
                            <li className="page-item disabled"><span className="page-link">…</span></li>
                          )}
                          <li className={`page-item ${n === safePage ? "active" : ""}`}>
                            <button className="page-link" onClick={() => goToPage(n)}>{n}</button>
                          </li>
                        </React.Fragment>
                      );
                    })}
                    <li className={`page-item ${safePage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => goToPage(safePage + 1)}>Next</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}